import React, { useState, useEffect } from "react";
import { Shield, ShieldAlert, KeyRound, Loader2, ArrowLeft, AlertTriangle, CheckCircle2 } from "lucide-react";
import { verifyTOTP, verifyAndConsumeBackupCode } from "../../services/totpService";

export default function MFALoginModal({
  isOpen,
  userEmail,
  mfaSecret,
  mfaBackupCodes = [],
  onSuccess,
  onCancel
}) {
  const [code, setCode] = useState("");
  const [isBackupMode, setIsBackupMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setCode("");
      setIsBackupMode(false);
      setError("");
      setLoading(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  async function handleVerify(e) {
    e.preventDefault();
    if (!code || !code.trim()) {
      setError(isBackupMode ? "Please enter your emergency recovery code." : "Please enter the 6-digit code.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      if (isBackupMode) {
        // Validate single-use recovery code
        const { valid, remainingHashedCodes } = await verifyAndConsumeBackupCode(code.trim(), mfaBackupCodes);
        if (!valid) {
          setError("Invalid emergency recovery code. Each code can only be used once.");
          setLoading(false);
          return;
        }

        onSuccess({ usedBackupCode: true, remainingBackupCodes: remainingHashedCodes });
      } else {
        // Validate standard 6-digit TOTP code
        const isValid = await verifyTOTP(mfaSecret, code.trim());
        if (!isValid) {
          setError("Invalid 6-digit code. Please verify the code in Google/Microsoft Authenticator and check your device clock.");
          setLoading(false);
          return;
        }

        onSuccess({ usedBackupCode: false });
      }
    } catch (err) {
      console.error("MFA verification error:", err);
      setError("An unexpected error occurred during verification. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modal-overlay animate-fade-in" style={{ zIndex: 1200 }}>
      <div 
        className="modal-content glass-card" 
        style={{ 
          maxWidth: "440px", 
          width: "95%", 
          padding: "2.5rem 2rem",
          position: "relative"
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "1.75rem" }}>
          <div 
            style={{ 
              width: "56px", 
              height: "56px", 
              borderRadius: "14px", 
              backgroundColor: "rgba(33, 150, 243, 0.15)",
              color: "var(--primary)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "1rem"
            }}
          >
            <Shield size={32} />
          </div>
          <h2 style={{ fontSize: "1.35rem", fontWeight: 700, margin: "0 0 0.35rem 0", color: "var(--text-primary)" }}>
            Two-Factor Authentication
          </h2>
          <p className="text-secondary" style={{ fontSize: "0.85rem", margin: 0 }}>
            {isBackupMode 
              ? "Enter one of your 8-character single-use emergency backup recovery codes."
              : `Enter the 6-digit code from Microsoft or Google Authenticator for ${userEmail || "your account"}.`
            }
          </p>
        </div>

        {error && (
          <div className="alert alert-danger" style={{ marginBottom: "1.25rem", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <AlertTriangle size={16} style={{ flexShrink: 0 }} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleVerify}>
          <div className="form-group" style={{ marginBottom: "1.75rem", textAlign: "center" }}>
            <input
              type="text"
              autoFocus
              maxLength={isBackupMode ? 10 : 6}
              placeholder={isBackupMode ? "XXXX-XXXX" : "000000"}
              value={code}
              onChange={(e) => {
                if (isBackupMode) {
                  setCode(e.target.value.toUpperCase());
                } else {
                  setCode(e.target.value.replace(/\D/g, ""));
                }
              }}
              style={{
                fontSize: isBackupMode ? "1.4rem" : "1.8rem",
                letterSpacing: isBackupMode ? "3px" : "8px",
                textAlign: "center",
                fontWeight: 700,
                height: "56px",
                width: "100%",
                maxWidth: isBackupMode ? "280px" : "240px",
                margin: "0 auto",
                borderRadius: "8px",
                border: "2px solid var(--primary)",
                backgroundColor: "var(--bg-main)",
                color: "var(--text-primary)"
              }}
            />
          </div>

          <button 
            type="submit" 
            className="btn btn-primary btn-block" 
            disabled={loading || (isBackupMode ? code.trim().length < 8 : code.trim().length !== 6)}
            style={{ width: "100%", height: "46px", marginBottom: "1rem" }}
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={18} style={{ marginRight: "0.5rem" }} />
                Verifying...
              </>
            ) : (
              "Verify & Sign In"
            )}
          </button>
        </form>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", textAlign: "center", marginTop: "1rem", borderTop: "1px solid var(--border-color)", paddingTop: "1rem" }}>
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={() => {
              setIsBackupMode(!isBackupMode);
              setCode("");
              setError("");
            }}
            style={{ fontSize: "0.85rem", justifyContent: "center" }}
          >
            {isBackupMode ? (
              "Use 6-digit Authenticator app code"
            ) : (
              <>
                <KeyRound size={14} style={{ marginRight: "0.4rem" }} />
                Use emergency backup code
              </>
            )}
          </button>

          <button 
            type="button" 
            onClick={onCancel}
            style={{ 
              background: "none", 
              border: "none", 
              color: "var(--text-muted)", 
              fontSize: "0.8rem", 
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.3rem"
            }}
          >
            <ArrowLeft size={14} />
            Cancel and Return to Sign In
          </button>
        </div>
      </div>
    </div>
  );
}
